/**
 * Central Time utilities for client-side date operations
 * Ensures consistency with server timezone for daily game operations
 */

/**
 * Get today's date string in Central Time (America/Chicago timezone)
 * This matches the server's date calculation for daily puzzles
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getCentralDateString = () => {
  const now = new Date();
  
  // Create a formatter for the America/Chicago timezone
  // Requesting specific parts is more robust than parsing a formatted string
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const parts = formatter.formatToParts(now);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  
  // The parts are guaranteed to be correct for the given timezone.
  // We just need to assemble them into YYYY-MM-DD format.
  return `${year}-${month}-${day}`;
};

/**
 * Get current Central Time Date object
 * @returns {Date} Date object representing current time in Central timezone
 */
export const getCentralDate = () => {
  const now = new Date();
  return new Date(now.toLocaleString("en-US", {
    timeZone: "America/Chicago"
  }));
};

/**
 * Check if two date strings represent the same day in Central Time
 * @param {string} date1 - First date string (YYYY-MM-DD)
 * @param {string} date2 - Second date string (YYYY-MM-DD)
 * @returns {boolean} True if dates are the same day
 */
export const isSameCentralDay = (date1, date2) => {
  return date1 === date2;
};

/**
 * Check if a saved state date is from today (Central Time)
 * @param {string} savedDate - Date string from saved state
 * @returns {boolean} True if the saved date is today
 */
export const isTodayInCentralTime = (savedDate) => {
  const today = getCentralDateString();
  return savedDate === today;
};